// src/screens/LegalScreen.js
import React from 'react';
import { ScrollView, Text, TouchableOpacity, StyleSheet, View } from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Ionicons } from '@expo/vector-icons';
import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { TOS_EFFECTIVE_DATE } from '../constants/tosUpdate';
import { openTermsOfService } from '../utils/openTermsOfService';

// NOTE (evergreen, Terms v1.7): Crits are retired and events are free to enter.
// Super Crits and Rumbles are purchases from CritCash; verified creators and
// players receive a share of them as compensation. Channel memberships are
// monthly purchases from CritCash; verified creators receive a share of each
// payment. Team memberships are monthly purchases from CritCash; the team's
// verified members who streamed with the team that month share each payment
// after the month ends. Unconditional (not flag-aware), like Terms 7: every
// membership line holds while memberships are dark ("Where a creator offers
// one", "Where a team offers one"). Part D of TOS_V1.7_DRAFT.md (the team
// blocks), STEP3_5A_TIERS_DESIGN.md 5.5 (the v1.6 tier and limit text) and
// TOS_V1.5_DRAFT.md. This explainer must never
// contradict the Terms (TosAgreementScreen.js / web/terms-of-service.html).
// Lexicon: follow the copy doctrine (skill-first, money-free fan copy).
// Empty until the release fills TOS_EFFECTIVE_DATE; the footer line hides while empty.
const POLICY_LAST_REVIEWED = TOS_EFFECTIVE_DATE;

export default function LegalScreen({ navigation }) {
  return (
    <View style={styles.outerContainer}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <Text style={styles.title}>Legal &amp; Eligibility</Text>
        <Text style={styles.subtitle}>
          Important information about participating on CritCast.
        </Text>
        <TouchableOpacity
          style={styles.termsLinkWrap}
          accessibilityRole="link"
          onPress={() => openTermsOfService(navigation)}
        >
          <Text style={styles.termsLink}>Read the full Terms of Service</Text>
        </TouchableOpacity>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Our Commitment to Fair Play</Text>
          <Text style={styles.bodyText}>
            By using CritCast, you agree to our Terms of Service and all platform
            rules. CritCast is a gameplay content platform: a place to stream
            your gameplay, compete, create and host events, and build leagues.
            How results are confirmed depends on the game, and all standings are
            subject to our fair-play protocols. Any form
            of cheating, fraud, or unsportsmanlike behavior may result in
            suspension and ineligibility for creator compensation.
          </Text>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Crits Are Retired</Text>
          <Text style={styles.bodyText}>
            <Text style={styles.bold}>Crits</Text>, CritCast's former entry and
            scoring system, are retired. Any Crits left on an account have no
            cash value, aren't needed for anything, and can't be bought,
            redeemed, or transferred.
          </Text>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Super Crits &amp; Rumbles</Text>
          <Text style={styles.bodyText}>
            Viewers can send <Text style={styles.bold}>Super Crits</Text> (paid
            highlighted messages on live streams) and{' '}
            <Text style={styles.bold}>Rumbles</Text>, which boost a live stream, a
            squad stream, or a live event. Both are purchases from CritCash, not
            transfers between users: CritCash keeps the full payment as its
            revenue, and the verified creators or players who receive them are
            credited a share as compensation (see Creator Compensation below).
            They can only go to verified creators and players, never to your own
            channel or squad or to an event you're entered in. Minimum and
            maximum amounts for each purchase are shown when you buy. If your
            account isn't verified, Super Crits, Rumbles and the first payment of
            a new membership together are limited to $500 in any 24 hours,
            counted for your account and for each card; verifying your account
            removes the limit. A card used in a disputed payment may be stopped
            from Super Crits, Rumbles and new memberships.
            {'\n\n'}
            A Rumble sent to an event is shared once the event's results are
            final. If the event is cancelled, no one in it can receive a share, or
            it isn't completed within the time we allow, the Rumble is refunded.
            {'\n\n'}
            Super Crits and Rumbles are never required to watch or participate,
            and they are final except where the law requires a refund, an event
            Rumble is refunded as described above, or we choose to issue a
            refund.
          </Text>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Channel Memberships</Text>
          <Text style={styles.bodyText}>
            Where a creator offers one, a channel membership is a monthly
            purchase from CritCash at one of up to four tiers the creator offers,
            with perks the creator picks for each tier, like a member badge in
            chat, members-only chat and members-only videos. It renews every
            month at your price for your tier until you cancel in Your
            memberships, and you keep your perks through the end of the month you
            paid for. You can switch to another tier the creator offers in Your
            memberships; the new tier starts at your next renewal. On an
            unverified account, a new membership's first payment counts toward
            the $500 limit above, and so does the difference in monthly price when
            you switch to a higher tier; renewals never do. Members-only chat is
            a mode the creator can switch on or off. A creator can end your
            membership to their channel; it then stops renewing and ends at the
            end of the month you paid for, and you lose members-only chat and the
            member badge there right away. The creator can see that you are a
            member, your tier, since when, and whether your membership is set to
            end. A chargeback on a membership payment ends that membership right
            away. If a card issuer reports a payment as fraud, or a payment is
            disputed as fraud, the memberships that payment or that account paid
            for with the card can end right away, and any other membership paid
            with that card can stop renewing at the end of the month already
            paid for.
          </Text>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Team Memberships</Text>
          <Text style={styles.bodyText}>
            Where a team offers one, a team membership is a monthly purchase from
            CritCash with perks the team's captain picks, like a team badge in
            squad chat and members-only squad chat. It renews every month until
            you cancel in Your memberships, and you keep your perks through the
            end of the month you paid for. If the team ends its team memberships,
            asks to disband, or falls below two people on its roster, your
            membership stops renewing and ends at the end of the month you paid
            for; if the team is saved before it disbands, we tell you, and you can
            keep your membership from Your memberships. The captain can end your
            membership to the team; it then stops renewing and ends at the end of
            the month you paid for, and you lose members-only squad chat and the
            team badge there right away. The team's captain is told when you join
            and can see that you are a member. You can't join a team you're on.
            On an unverified account, a new team membership's first payment
            counts toward the $500 limit above; renewals never do. A chargeback
            on a membership payment ends that membership right away.
          </Text>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Creator Compensation</Text>
          <Text style={styles.bodyText}>
            Verified creators and players may qualify for{' '}
            <Text style={styles.bold}>creator compensation</Text>, which CritCash
            pays from its own revenue. It comes only from Super Crits, Rumbles and
            channel and team memberships.
            {'\n\n'}
            A verified creator or player who receives a Super Crit or a Rumble is
            credited a share of it, set by CritCash. The share is currently up to
            70% of the purchase price, after payment-processing costs, so it can
            be lower, especially on smaller purchases, and it is divided when a
            purchase is shared; the current share is shown in the product. A
            Super Crit or a Rumble sent to a squad is shared equally among the
            squad members who are verified, linked to the squad, and streaming
            when it's sent. A Rumble sent to an event is shared equally among the
            verified players who played in it, whatever their placement (other
            than the person who sent it), once its results are final. A verified
            creator is also credited a share of each membership payment to their
            channel, including renewals, on the same terms. A team membership
            payment is shared, after the end of the month it was paid in, among
            the team's verified members who streamed with the team in that month,
            on the same terms; the person who paid is never included. While an account is suspended, under
            review, or on a payment hold, it can't receive Rumbles and isn't
            included in squad or event shares of them. A credit recorded when the
            payment succeeds, or when an event's results are final, stays pending
            until at least 48 hours after the purchase before it becomes payable.
            If one viewer's Super Crits and Rumbles, or those paid with one card,
            add up to an unusually large amount within 24 hours, the share from
            the purchase that reaches that amount, and from later ones in those 24
            hours, may stay pending for up to 30 days while we review them.
            Payable earnings pay out automatically to the payout account you
            connect, subject to a minimum amount and a schedule we set.
            {'\n\n'}
            Creator compensation is CritCash paying its own revenue for
            engagement and entertainment. It is{' '}
            <Text style={styles.bold}>not</Text> a transfer of any purchaser's
            money, and a refund or dispute alone never reduces what a creator or
            player was already credited. Payment is conditional on verification
            of your identity and age, may require tax information, and depends on
            following platform rules.
          </Text>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Decided by Skill</Text>
          <Text style={styles.bodyText}>
            CritCast events are decided entirely by player skill, strategy,
            reaction time, and game knowledge, never by chance. You never pay
            cash to participate: events and leagues are free to enter, and
            CritCash does not hold or custody player funds. Results and
            placements are never paid: compensation comes only from Super Crits,
            Rumbles and channel and team memberships (see Creator Compensation above). The better player wins.
            On supported games, results are verified from the stream; on other
            games, players report results, and disputed results are reviewed.
          </Text>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Eligibility &amp; Age</Text>
          <Text style={styles.bodyText}>
            You must be{' '}
            <Text style={styles.bold}>18 years of age or older</Text> to create
            an account and use CritCast. It is an adults-only platform.
            {'\n\n'}
            CritCast is{' '}
            <Text style={styles.bold}>open worldwide</Text>. Availability may vary
            by region, and it remains your responsibility to follow the laws where
            you are physically located.
          </Text>
          <View style={styles.infoBox}>
            <Ionicons name="information-circle-outline" size={20} color="#888" />
            <Text style={styles.infoText}>
              Available worldwide. All amounts are shown in USD.
            </Text>
          </View>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Copyright &amp; Content Reports</Text>
          <Text style={styles.bodyText}>
            Players own the content they create, are responsible for having the
            rights to everything they broadcast or publish, and grant CritCash
            the license needed to host and display it. If you believe content on
            CritCast infringes your copyright, use the{' '}
            <Text style={styles.bold}>Report</Text> option on the video or
            contact support@critcash.com with a description of the work, where
            the material appears, and your contact information. We remove
            infringing content and, in appropriate circumstances, terminate
            repeat infringers.
          </Text>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Your Responsibility</Text>
          <Text style={styles.bodyText}>
            Laws regarding online competition vary and change over time. It is
            ultimately your responsibility to understand and comply with the laws
            where you are physically located when you use our service.
            {'\n\n'}
            <Text style={styles.bold}>Do not attempt to circumvent eligibility
            checks.</Text> Doing so violates our Terms of Service and may result in
            account termination.
          </Text>
        </View>

        <View style={styles.contentBlock}>
          <Text style={styles.header}>Publisher Compliance</Text>
          <Text style={styles.bodyText}>
            CritCash operates in compliance with game publisher guidelines and
            community competition licenses. Competitions using specific game
            titles are subject to the terms and conditions set forth by their
            respective publishers. CritCash is not affiliated with or sponsored by
            any game publisher unless explicitly stated.
          </Text>
        </View>

        {POLICY_LAST_REVIEWED ? (
          <View style={styles.policyInfo}>
            <Text style={styles.policyText}>
              Policy last reviewed: {POLICY_LAST_REVIEWED}
            </Text>
          </View>
        ) : null}

        <TouchableOpacity
          style={styles.button}
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
        >
          <Ionicons name="arrow-back" size={18} color="#000" style={styles.buttonIcon} />
          <Text style={styles.buttonText}>Go Back</Text>
        </TouchableOpacity>
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
      <StatusBar style="light" />
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: '#000',
  },
  scrollContainer: {
    flexGrow: 1,
    alignItems: 'center',
    padding: 20,
    paddingBottom: 60,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FCD000',
    marginBottom: 10,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#CCC',
    textAlign: 'center',
    marginBottom: 12,
    paddingHorizontal: 10,
  },
  termsLinkWrap: {
    marginBottom: 30,
  },
  termsLink: {
    fontSize: 14,
    color: '#FCD000',
    textDecorationLine: 'underline',
    textAlign: 'center',
  },
  contentBlock: {
    width: '100%',
    maxWidth: 800,
    backgroundColor: '#111',
    borderRadius: 8,
    padding: 15,
    marginBottom: 20,
  },
  header: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FCD000',
    marginBottom: 10,
  },
  bodyText: {
    fontSize: 14,
    color: '#AAA',
    lineHeight: 22,
  },
  listContainer: {
    marginTop: 10,
  },
  listItem: {
    fontSize: 14,
    color: '#AAA',
    lineHeight: 22,
    marginBottom: 10,
    paddingLeft: 4,
  },
  bold: {
    fontWeight: 'bold',
    color: '#DDD',
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    padding: 12,
    borderRadius: 8,
    marginTop: 10,
    gap: 10,
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    color: '#888',
    lineHeight: 20,
  },
  policyInfo: {
    marginTop: 10,
    marginBottom: 10,
  },
  policyText: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
  },
  button: {
    marginTop: 20,
    backgroundColor: '#FCD000',
    paddingVertical: 12,
    paddingHorizontal: 30,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  buttonIcon: {
    marginRight: 4,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',
  },
});
